import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser, selectIsLoggedIn } from "../store/userSlice";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Container,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import MKBox from "../components/MKBox";
import MKTypography from "../components/MKTypography";
import MKButton from "../components/MKButton";
import ChessRegistrationModal from "../components/ChessRegistrationModal";
import DndRegistrationModal from "../components/DndRegistrationModal";
import API_BASE_URL from "../apiConfig";
import { calculateFee } from "../utils/feeCalculator";
import { useAuthModal } from "../contexts/AuthModalContext";
import {
  PRIMARY_PINK,
  PRIMARY_PINK_DARK,
  SUCCESS_GREEN,
  WARNING_ORANGE,
  TRANSPARENT_PINK_05,
  TRANSPARENT_PINK_1,
  TRANSPARENT_PINK_3,
  TRANSPARENT_PINK_5,
  BORDER_LIGHT,
} from "../constants";

function EventLandingPage() {
  const theme = useTheme();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { openSignIn } = useAuthModal();
  const [event, setEvent] = useState(null);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [purchasing, setPurchasing] = useState(false);

  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [userRsvpStatus, setUserRsvpStatus] = useState(null);
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [userRequestStatus, setUserRequestStatus] = useState(null);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [chessModalOpen, setChessModalOpen] = useState(false);
  const [chessFormPendingAction, setChessFormPendingAction] = useState(null); // 'rsvp' or 'purchase'
  const [chessFormLoading, setChessFormLoading] = useState(false);
  const [dndModalOpen, setDndModalOpen] = useState(false);
  const [dndFormPendingAction, setDndFormPendingAction] = useState(null); // 'rsvp' or 'purchase'
  const [dndFormLoading, setDndFormLoading] = useState(false);

  const ticketLimit = event?.ticket_quantity != null && event.ticket_quantity !== ""
    ? parseInt(event.ticket_quantity, 10)
    : null;
  const ticketsSold = parseInt(event?.total_tickets_sold || 0, 10);
  const ticketsRemaining = ticketLimit ? Math.max(0, ticketLimit - ticketsSold) : null;
  const isSoldOut = ticketsRemaining !== null && ticketsRemaining <= 0;
  const maxPurchaseQuantity = ticketsRemaining !== null ? Math.min(10, ticketsRemaining) : 10;

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Event not found");
        }

        const eventData = await response.json();
        setEvent(eventData);

        // Fetch club details if we have club_id
        if (eventData.club_id) {
          const clubResponse = await fetch(`${API_BASE_URL}/api/clubs/${eventData.club_id}`, {
            credentials: "include",
          });
          if (clubResponse.ok) {
            const clubData = await clubResponse.json();
            setClub(clubData);
          }
        }

        // Clear success message when event changes
        setSuccess("");

      } catch (error) {
        console.error("Error fetching event:", error);
        setError("Failed to load event details");
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  useEffect(() => {
    // Check user's RSVP status if RSVP is enabled and user is logged in
    if (event?.rsvp_enabled && user?.id && isLoggedIn) {
      checkUserRsvpStatus();
      checkWaitlistStatus();
    }
  }, [event, user, isLoggedIn]);

  useEffect(() => {
    // Check user's request status if event is private and user is logged in
    if (event?.private_event && user?.id && isLoggedIn) {
      checkUserRequestStatus();
    }
  }, [event, user, isLoggedIn]);

  useEffect(() => {
    if (ticketsRemaining !== null && ticketQuantity > ticketsRemaining) {
      setTicketQuantity(Math.max(1, ticketsRemaining));
    }
  }, [ticketsRemaining, ticketQuantity]);

  const checkConflicts = async () => {
    if (!event?.rsvp_enabled || !user?.id || !isLoggedIn) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/conflicts?user_id=${user.id}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.conflictInfo && data.conflictInfo.hasConflicts) {
          setConflictInfo(data.conflictInfo);
        } else {
          setConflictInfo(null);
        }
      }
    } catch (error) {
      console.error("Error checking conflicts:", error);
    }
  };

  const checkWaitlistStatus = async () => {
    if (!event?.rsvp_enabled || !user?.id || !isLoggedIn) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/waitlist/${user.id}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.on_waitlist) {
          setWaitlistStatus({
            on_waitlist: true,
            position: data.position
          });
        } else {
          setWaitlistStatus(null);
        }
      }
    } catch (error) {
      console.error("Error checking waitlist status:", error);
    }
  };

  const checkUserRsvpStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/rsvp/status?user_id=${user.id}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserRsvpStatus(data);
        
        // If user is RSVP'd, check for conflicts
        if (data.has_rsvped) {
          await checkConflicts();
        } else {
          // Clear conflicts if user is not RSVP'd
          setConflictInfo(null);
        }
      }
    } catch (error) {
      console.error("Error checking RSVP status:", error);
    }
  };

  const checkUserRequestStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/request/status?user_id=${user.id}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserRequestStatus(data);
      }
    } catch (error) {
      console.error("Error checking request status:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleBackToClub = () => {
    if (club?.handle) {
      navigate(`/clubs/${club.handle}`);
    } else {
      navigate("/clubs");
    }
  };

  const handlePurchaseTickets = async (chessData = null, dndData = null) => {
    if (!event || !event.ticket_available || isSoldOut) return;
    if (ticketsRemaining !== null && ticketQuantity > ticketsRemaining) return;

    if (!isLoggedIn) {
      openSignIn();
      return;
    }
    
    // If it's a chess event and we don't have chess data yet, show the modal
    if (event.chess_event && !chessData) {
      setChessFormPendingAction('purchase');
      setChessModalOpen(true);
      return;
    }

    // If it's a DnD event and we don't have DnD data yet, show the modal
    if (event.dnd_event && !dndData) {
      setDndFormPendingAction('purchase');
      setDndModalOpen(true);
      return;
    }
    
    // Navigate to checkout page with event data
    navigate(`/checkout/${event.id}`, {
      state: {
        quantity: ticketQuantity,
        chessRegistration: chessData,
        dndRegistration: dndData,
      },
    });
  };

  const handleRSVP = async (chessData = null, dndData = null) => {
    if (!event?.rsvp_enabled || !user?.id || !isLoggedIn) return;
    
    // If it's a chess event and we don't have chess data yet, show the modal
    if (event.chess_event && !chessData) {
      setChessFormPendingAction('rsvp');
      setChessModalOpen(true);
      return;
    }

    // If it's a DnD event and we don't have DnD data yet, show the modal
    if (event.dnd_event && !dndData) {
      setDndFormPendingAction('rsvp');
      setDndModalOpen(true);
      return;
    }
    
    setRsvpLoading(true);
    try {
      const requestBody = {
        user_id: user.id
      };
      
      // Include chess data if provided
      if (chessData) {
        requestBody.chess_registration = chessData;
      }

      // Include DnD data if provided
      if (dndData) {
        requestBody.dnd_registration = dndData;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to RSVP');
      }

      const responseData = await response.json();
      
      // Check for scheduling conflicts
      if (responseData.conflictInfo && responseData.conflictInfo.hasConflicts) {
        setConflictInfo(responseData.conflictInfo);
      } else {
        setConflictInfo(null);
      }

      // Refresh RSVP status
      await checkUserRsvpStatus();
      await checkWaitlistStatus();
      
      // Refresh event data to update counts
      const eventResponse = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
        credentials: "include",
      });
      if (eventResponse.ok) {
        const eventData = await eventResponse.json();
        setEvent(eventData);
      }
    } catch (error) {
      console.error('Error RSVPing:', error);
      setError('Failed to RSVP: ' + error.message);
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleChessFormSubmit = async (chessData) => {
    setChessFormLoading(true);
    try {
      if (chessFormPendingAction === 'rsvp') {
        // Close modal and proceed with RSVP
        setChessModalOpen(false);
        await handleRSVP(chessData);
      } else if (chessFormPendingAction === 'purchase') {
        // Close modal and proceed with ticket purchase
        setChessModalOpen(false);
        await handlePurchaseTickets(chessData);
      }
      setChessFormPendingAction(null);
    } catch (error) {
      console.error('Error submitting chess form:', error);
      setError('Failed to submit chess registration: ' + error.message);
    } finally {
      setChessFormLoading(false);
    }
  };

  const handleChessModalClose = () => {
    setChessModalOpen(false);
    setChessFormPendingAction(null);
  };

  const handleDndFormSubmit = async (dndData) => {
    setDndFormLoading(true);
    try {
      if (dndFormPendingAction === 'rsvp') {
        setDndModalOpen(false);
        await handleRSVP(null, dndData);
      } else if (dndFormPendingAction === 'purchase') {
        setDndModalOpen(false);
        await handlePurchaseTickets(null, dndData);
      }
      setDndFormPendingAction(null);
    } catch (error) {
      console.error('Error submitting DnD form:', error);
      setError('Failed to submit DnD registration: ' + error.message);
    } finally {
      setDndFormLoading(false);
    }
  };

  const handleDndModalClose = () => {
    setDndModalOpen(false);
    setDndFormPendingAction(null);
  };

  const handleCancelRSVP = async () => {
    if (!event?.rsvp_enabled || !user?.id || !isLoggedIn) return;
    
    setRsvpLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/rsvp`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: user.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel RSVP');
      }

      // Clear conflict info and waitlist status since user is no longer RSVP'd
      setConflictInfo(null);
      setWaitlistStatus(null);
      
      // Refresh RSVP status
      await checkUserRsvpStatus();
      await checkWaitlistStatus();
      
      // Refresh event data to update counts
      const eventResponse = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
        credentials: "include",
      });
      if (eventResponse.ok) {
        const eventData = await eventResponse.json();
        setEvent(eventData);
      }
    } catch (error) {
      console.error('Error cancelling RSVP:', error);
      setError('Failed to cancel RSVP: ' + error.message);
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleRequestToJoin = async () => {
    if (!event?.private_event || !user?.id || !isLoggedIn) return;
    
    setRequestLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: user.id,
          message: '' // No message needed
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit request');
      }

      // Refresh request status
      await checkUserRequestStatus();
      setSuccess('Request submitted successfully!');
    } catch (error) {
      console.error('Error submitting request:', error);
      setError('Failed to submit request: ' + error.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleRemoveRequest = async () => {
    if (!event?.private_event || !user?.id || !isLoggedIn || !userRequestStatus?.request?.id) return;
    
    if (!window.confirm("Are you sure you want to remove yourself from this event? This action cannot be undone.")) {
      return;
    }
    
    setRequestLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/requests/${userRequestStatus.request.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove request');
      }

      // Refresh request status
      await checkUserRequestStatus();
      setSuccess('Successfully removed yourself from the event!');
    } catch (error) {
      console.error('Error removing request:', error);
      setError('Failed to remove request: ' + error.message);
    } finally {
      setRequestLoading(false);
    }
  };

  if (loading) {
    return (
      <MKBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </MKBox>
    );
  }

  if (error || !event) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || "Event not found"}
        </Alert>
        <MKButton
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/clubs")}
        >
          Back to Clubs
        </MKButton>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <MKBox sx={{ mb: 3 }}>
        <MKButton
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToClub}
          sx={{ 
            mb: 2,
            color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
            borderColor: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
            '&:hover': {
              borderColor: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
            }
          }}
        >
          Back to {club?.name || "Club"}
        </MKButton>

        <Typography variant="h3" component="h1" gutterBottom sx={{ color: PRIMARY_PINK }}>
          {event.title}
        </Typography>


      </MKBox>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Event Details */}
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ color: PRIMARY_PINK }}>
                Event Details
              </Typography>
              
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  mb: 3,
                  whiteSpace: 'pre-line'
                }}
              >
                {event.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Event Info */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CalendarIcon sx={{ color: PRIMARY_PINK }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(event.start_time)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <ScheduleIcon sx={{ color: PRIMARY_PINK }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Time
                    </Typography>
                    <Typography variant="body1">
                      {formatTime(event.start_time)} - {formatTime(event.end_time)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <LocationIcon sx={{ color: PRIMARY_PINK }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1">
                      {event.private_event ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Location hidden for private event
                          </Typography>
                        </Box>
                      ) : event.hide_location_until_rsvp && (!userRsvpStatus?.has_rsvped) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Location hidden until RSVP'd
                          </Typography>
                          {event.rsvp_enabled && (
                            <MKButton
                              variant="outlined"
                              size="small"
                              onClick={() => handleRSVP()}
                              disabled={rsvpLoading}
                              sx={{ ml: 1 }}
                            >
                              RSVP to See Location
                            </MKButton>
                          )}
                        </Box>
                      ) : event.location_url ? (
                        <Typography
                          component="a"
                          href={event.location_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: PRIMARY_PINK,
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {event.location_display_name || event.location || 'TBD'}
                        </Typography>
                      ) : (
                        event.location || 'Location TBD'
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Event Flyer */}
          {event.flyer_url && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  Event Flyer
                </Typography>
                <Box
                  component="img"
                  src={event.flyer_url}
                  alt="Event flyer"
                  sx={{
                    width: "100%",
                    maxWidth: 600,
                    height: "auto",
                    borderRadius: 1,
                    display: "block",
                    margin: "0 auto",
                  }}
                />
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={5}>
                    {/* RSVP Section - Only show if RSVP is enabled, user is logged in, and event is NOT private */}
          {event.rsvp_enabled && !event.private_event && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  RSVP for Free Event
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: SUCCESS_GREEN }}>
                    FREE
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This is a free event. RSVP to secure your spot!
                  </Typography>
                </Box>

                {event.rsvp_limit && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: TRANSPARENT_PINK_05, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>RSVP Limit:</strong> {event.rsvp_count || 0} / {event.rsvp_limit} people RSVPed
                    </Typography>
                  </Box>
                )}

                {event.rsvp_deadline && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: TRANSPARENT_PINK_05, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>RSVP Deadline:</strong> {formatDate(event.rsvp_deadline)} at {formatTime(event.rsvp_deadline)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Conflict Warning Alert */}
                {conflictInfo && conflictInfo.hasConflicts && (
                  <Alert 
                    severity="warning" 
                    sx={{ mb: 2 }}
                    onClose={() => setConflictInfo(null)}
                  >
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      ⚠️ Scheduling Conflict Detected
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      You have successfully RSVPed, but this event conflicts with another event you're already attending:
                    </Typography>
                    {conflictInfo.conflictingEvents.map((conflict, index) => (
                      <Box key={index} sx={{ mt: 1, pl: 2, borderLeft: `3px solid ${WARNING_ORANGE}` }}>
                        <Typography variant="body2" fontWeight="bold">
                          {conflict.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {conflict.club_name} • {new Date(conflict.start_time).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Please consider canceling one of your RSVPs if you cannot attend both events.
                    </Typography>
                  </Alert>
                )}

                {!isLoggedIn ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Please log in to RSVP for this event
                    </Typography>
                    <MKButton
                      variant="outlined"
                      onClick={openSignIn}
                      fullWidth
                    >
                      Log In to RSVP
                    </MKButton>
                  </Box>
                ) : waitlistStatus?.on_waitlist ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <ScheduleIcon sx={{ fontSize: 48, color: WARNING_ORANGE, mb: 1 }} />
                    <Typography variant="h6" sx={{ color: WARNING_ORANGE, mb: 1 }}>
                      You're on the Waitlist
                    </Typography>
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(255, 167, 38, 0.1)', borderRadius: 1 }}>
                      <Typography variant="h5" fontWeight="bold" sx={{ color: WARNING_ORANGE, mb: 1 }}>
                        Position #{waitlistStatus.position}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        You'll be automatically added when a spot opens up. We'll notify you by email!
                      </Typography>
                    </Box>
                    <MKButton
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        // TODO: Add remove from waitlist functionality
                        console.log('Remove from waitlist');
                      }}
                      fullWidth
                      sx={{
                        borderColor: 'error.main',
                        color: 'error.main',
                        "&:hover": {
                          borderColor: 'error.dark',
                          backgroundColor: "rgba(211, 47, 47, 0.04)"
                        }
                      }}
                    >
                      Remove Me from Waitlist
                    </MKButton>
                  </Box>
                ) : userRsvpStatus?.has_rsvped ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: SUCCESS_GREEN, mb: 1 }} />
                    <Typography variant="h6" sx={{ color: SUCCESS_GREEN, mb: 1 }}>
                      You're RSVPed!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      You have RSVPed for this event
                    </Typography>
                    <MKButton
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelRSVP}
                      disabled={rsvpLoading}
                      fullWidth
                    >
                      {rsvpLoading ? "Cancelling..." : "Cancel RSVP"}
                    </MKButton>
                  </Box>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                      Click below to RSVP for this free event
                    </Typography>

                    <MKButton
                      variant="contained"
                      fullWidth
                      onClick={() => handleRSVP()}
                      disabled={rsvpLoading}
                      startIcon={rsvpLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                      sx={{
                        bgcolor: SUCCESS_GREEN,
                        "&:hover": {
                          bgcolor: "#2e7d32",
                        },
                      }}
                    >
                      {rsvpLoading ? "RSVPing..." : "RSVP Now"}
                    </MKButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Private Event Request Section */}
          {event.private_event && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Private Event - Request to Join
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This is a private event. You must request permission to join. Once approved, you'll be able to see the event location and RSVP.
                  </Typography>
                </Box>

                {!isLoggedIn ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Please log in to request to join this event
                    </Typography>
                    <MKButton
                      variant="outlined"
                      onClick={openSignIn}
                      fullWidth
                    >
                      Log In to Request
                    </MKButton>
                  </Box>
                ) : userRequestStatus?.has_requested ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      You have already requested to join this event
                    </Typography>
                    <Chip
                      label={userRequestStatus.request?.status || 'Pending'}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: userRequestStatus.request?.status === 'approved' ? SUCCESS_GREEN : 
                                   userRequestStatus.request?.status === 'rejected' ? WARNING_ORANGE : PRIMARY_PINK,
                        color: userRequestStatus.request?.status === 'approved' ? SUCCESS_GREEN : 
                               userRequestStatus.request?.status === 'rejected' ? WARNING_ORANGE : PRIMARY_PINK,
                      }}
                    />
                    
                    {/* Self-removal option for approved users */}
                    {userRequestStatus.request?.status === 'approved' && (
                      <Box sx={{ mt: 2 }}>
                        <MKButton
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={handleRemoveRequest}
                          disabled={requestLoading}
                          fullWidth
                          sx={{
                            borderColor: WARNING_ORANGE,
                            color: WARNING_ORANGE,
                            "&:hover": {
                              borderColor: "#f57c00",
                              backgroundColor: "rgba(245, 124, 0, 0.04)"
                            }
                          }}
                        >
                          {requestLoading ? "Removing..." : "Remove Myself from Event"}
                        </MKButton>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    <MKButton
                      variant="contained"
                      fullWidth
                      onClick={handleRequestToJoin}
                      disabled={requestLoading}
                      startIcon={requestLoading ? <CircularProgress size={20} color="inherit" /> : <GroupIcon />}
                      sx={{
                        bgcolor: PRIMARY_PINK,
                        color: '#ffffff',
                        "&:hover": {
                          bgcolor: PRIMARY_PINK_DARK,
                        },
                      }}
                    >
                      {requestLoading ? "Submitting..." : "Request to Join"}
                    </MKButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Registration Section - Only show if registration URL is present */}
          {(event.registration_url || event.iframe_data) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  Registration
                </Typography>

                {/* Embedded Registration */}
                {event.iframe_data && event.startgg_event && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Register for this event:
                    </Typography>
                    <Box
                      dangerouslySetInnerHTML={{ __html: event.iframe_data }}
                      sx={{
                        width: "100%",
                        minHeight: 800,
                        maxHeight: 1000,
                        border: `1px solid ${BORDER_LIGHT}`,
                        borderRadius: 1,
                        overflow: "hidden",
                        "& iframe": {
                          width: "100% !important",
                          height: "800px !important",
                          border: "none",
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>
                )}

                {/* External Registration Link */}
                {event.registration_url && !event.iframe_data && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Register for this event:
                    </Typography>
                    <MKButton
                      variant="contained"
                      fullWidth
                      href={event.registration_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: PRIMARY_PINK,
                        color: '#ffffff',
                        "&:hover": {
                          bgcolor: PRIMARY_PINK_DARK,
                        },
                      }}
                    >
                      Register Now
                    </MKButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Ticket Purchase Section */}
          {event.ticket_available && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Purchase Tickets
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: PRIMARY_PINK }}>
                    ${parseFloat(event.ticket_price || 0).toFixed(2)} {event.ticket_currency}
                  </Typography>
                  {event.ticket_description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {event.ticket_description}
                    </Typography>
                  )}
                  

                </Box>

                <Divider sx={{ my: 2 }} />

                {isSoldOut ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: WARNING_ORANGE, mb: 1 }}>
                      Sold Out
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      All tickets for this event have been sold.
                    </Typography>
                    <MKButton
                      variant="contained"
                      fullWidth
                      disabled
                      startIcon={<ShoppingCartIcon />}
                      sx={{
                        bgcolor: PRIMARY_PINK,
                        color: '#ffffff',
                        "&.Mui-disabled": {
                          bgcolor: "rgba(0, 0, 0, 0.12)",
                          color: "rgba(0, 0, 0, 0.38)",
                        },
                      }}
                    >
                      Sold Out
                    </MKButton>
                  </Box>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      type="number"
                      label="Quantity"
                      value={ticketQuantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (value >= 1 && value <= maxPurchaseQuantity) {
                          setTicketQuantity(value);
                        }
                      }}
                      inputProps={{
                        min: 1,
                        max: maxPurchaseQuantity,
                        step: 1,
                      }}
                      fullWidth
                      sx={{
                        mb: 2,
                        '& .MuiInputBase-input': {
                          color: 'text.primary',
                          py: 1.5,
                        },
                                                '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: TRANSPARENT_PINK_3,
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: TRANSPARENT_PINK_5,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: PRIMARY_PINK,
                          },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                          opacity: 1,
                        },
                      }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">Total:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ${(parseFloat(event.ticket_price || 0) * ticketQuantity).toFixed(2)} {event.ticket_currency}
                      </Typography>
                    </Box>

                    <MKButton
                      variant="contained"
                      fullWidth
                      onClick={() => handlePurchaseTickets()}
                      disabled={purchasing}
                      startIcon={purchasing ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />}
                      sx={{
                        bgcolor: PRIMARY_PINK,
                        color: '#ffffff',
                        "&:hover": {
                          bgcolor: PRIMARY_PINK_DARK,
                        },
                      }}
                    >
                      {purchasing ? "Processing..." : "Purchase Tickets"}
                    </MKButton>
                  </Box>
                )}

                {ticketLimit && !isSoldOut && (
                  <Typography variant="caption" color="text.secondary">
                    {ticketsRemaining} ticket{ticketsRemaining === 1 ? '' : 's'} remaining
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Attendees Section */}
          {event.attendee_count > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  {event.rsvp_enabled ? (
                    `RSVPs (${event.rsvp_count || 0} ${(event.rsvp_count || 0) === 1 ? 'person' : 'people'})`
                  ) : (
                    `Attendees (${event.total_tickets_sold} tickets sold)`
                  )}
                </Typography>
                
                {event.hide_rsvp_list && event.rsvp_enabled ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      RSVP list is hidden from public view
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {event.attendees && event.attendees.length > 0 ? (
                      event.attendees.map((attendee, index) => (
                        <Box
                          key={attendee.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1,
                            px: 1,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: index % 2 === 0 ? TRANSPARENT_PINK_05 : 'transparent',
                            '&:hover': {
                              bgcolor: TRANSPARENT_PINK_1,
                            },
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {attendee.display_name || attendee.email}
                            </Typography>
                            {attendee.rsvp_type !== 'rsvp' && (
                              <Typography variant="caption" color="text.secondary">
                                {attendee.ticket_quantity} ticket{attendee.ticket_quantity > 1 ? 's' : ''} • ${attendee.ticket_price} {attendee.ticket_currency}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={attendee.attendee_status}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: attendee.attendee_status === 'confirmed' ? SUCCESS_GREEN : WARNING_ORANGE,
                              color: attendee.attendee_status === 'confirmed' ? SUCCESS_GREEN : WARNING_ORANGE,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {event.rsvp_enabled ? 'No RSVPs yet.' : 'No attendees yet.'}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Entrants Section (for Start.gg events) */}
          {event.startgg_event_id && event.startgg_entrants && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  Start.gg Entrants ({event.startgg_entrants.length})
                </Typography>
                
                {event.startgg_entrants.length > 0 ? (
                  <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {event.startgg_entrants.map((entrant, index) => (
                      <Box
                        key={entrant.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          px: 1,
                          mb: 1,
                          borderRadius: 1,
                          bgcolor: index % 2 === 0 ? TRANSPARENT_PINK_05 : 'transparent',
                          '&:hover': {
                            bgcolor: TRANSPARENT_PINK_1,
                          },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {entrant.name}
                          </Typography>
                          {entrant.participants && entrant.participants.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {entrant.participants.map(participant => 
                                `${participant.prefix || ''}${participant.gamerTag || 'Unknown'}`
                              ).join(', ')}
                            </Typography>
                          )}
                        </Box>
                        {entrant.seeds && entrant.seeds.length > 0 && entrant.seeds[0]?.seedNum && (
                          <Chip
                            label={`Seed ${entrant.seeds[0].seedNum}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: PRIMARY_PINK,
                              color: PRIMARY_PINK,
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No entrants registered yet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}


        </Grid>
      </Grid>

      {/* Chess Registration Modal */}
      <ChessRegistrationModal
        open={chessModalOpen}
        onClose={handleChessModalClose}
        onSubmit={handleChessFormSubmit}
        loading={chessFormLoading || rsvpLoading || purchasing}
      />
      <DndRegistrationModal
        open={dndModalOpen}
        onClose={handleDndModalClose}
        onSubmit={handleDndFormSubmit}
        loading={dndFormLoading || rsvpLoading || purchasing}
      />
    </Container>
  );
}

export default EventLandingPage; 